import { create as createQr } from "qrcode";
import {
  buildArtikelEtikettUrl,
  dotsToMm,
  getTapeSpec,
  mmToDots,
  type ArtikelEtikettItem,
  type ArtikelEtikettLocation,
  type TapeSpec,
} from "@/lib/artikelEtikett";

export interface RenderedArtikelEtikett {
  item: ArtikelEtikettItem;
  qrUrl: string;
  canvas: HTMLCanvasElement;
  dataUrl: string;
  widthDots: number;
  heightDots: number;
  widthMm: number;
  heightMm: number;
}

export interface ArtikelEtikettLayout {
  padding: number;
  gap: number;
  qrSize: number;
  qrX: number;
  qrY: number;
  textX: number;
  textY: number;
  textWidth: number;
  textHeight: number;
  widthDots: number;
  heightDots: number;
}

export type EtikettFontWeight = 600 | 700;

const ETIKETT_FONT = '"Segoe UI", system-ui, sans-serif';
const LINE_HEIGHT = 1.16;
const MIN_NR_FONT = 8;
const MIN_NAME_FONT = 7;
const MIN_TEXT_MM = 18;
/** Großer QR bleibt scanbar, lässt auf 24/36-mm-Band aber Platz für den Text. */
const MAX_QR_MM = 20;

export function planArtikelEtikettLayout(spec: TapeSpec, lengthMm: number): ArtikelEtikettLayout {
  const widthDots = Math.max(1, mmToDots(lengthMm));
  const padding = Math.max(6, Math.round(spec.printDots * 0.05));
  const gap = Math.max(10, Math.round(spec.printDots * 0.06));
  const minText = mmToDots(MIN_TEXT_MM);
  const maxQr = Math.max(24, spec.printDots - padding * 2);
  const qrCap = spec.tapeMm >= 24 ? Math.min(maxQr, mmToDots(MAX_QR_MM)) : maxQr;
  const qrSize = Math.max(24, Math.min(qrCap, widthDots - padding * 2 - gap - minText));
  const textX = padding + qrSize + gap;
  const textWidth = Math.max(8, widthDots - textX - padding);
  const textHeight = Math.max(8, spec.printDots - padding * 2);
  return {
    padding,
    gap,
    qrSize,
    qrX: padding,
    qrY: Math.round((spec.printDots - qrSize) / 2),
    textX,
    textY: padding,
    textWidth,
    textHeight,
    widthDots,
    heightDots: spec.printDots,
  };
}

export function maxEtikettNameLines(boxH: number): number {
  if (boxH >= 340) return 6;
  if (boxH >= 250) return 5;
  if (boxH >= 190) return 4;
  if (boxH >= 130) return 3;
  return 2;
}

function nameBlockHeight(nameFont: number, lineCount: number): number {
  return lineCount * nameFont * LINE_HEIGHT;
}

function etikettGap(nrFont: number, nameFont: number): number {
  return Math.max(4, Math.round(Math.min(nrFont, nameFont) * 0.22));
}

export function fitEtikettFonts(
  measure: (fontPx: number, weight: EtikettFontWeight, text: string) => number,
  heroNr: string,
  name: string,
  boxW: number,
  boxH: number,
): { nrFontPx: number; nameFontPx: number; nameLines: string[]; blockHeight: number } {
  const nrText = heroNr.trim() || "—";
  const nameText = name.trim() || "—";
  const maxLines = maxEtikettNameLines(boxH);
  const maxNrByHeight = Math.max(MIN_NR_FONT, Math.floor(boxH * 0.4));

  const packName = (nameFont: number, linesAllowed: number, allowEllipsis: boolean) => {
    if (allowEllipsis) {
      const nameLines = wrapEtikettText((text) => measure(nameFont, 600, text), nameText, boxW, linesAllowed);
      return { nameLines, complete: nameLines.join(" ").replace(/…$/, "") === nameText };
    }
    const nameLines = wrapEtikettTextComplete((text) => measure(nameFont, 600, text), nameText, boxW, linesAllowed);
    return nameLines ? { nameLines, complete: true } : { nameLines: [] as string[], complete: false };
  };

  const largestFont = (
    min: number,
    max: number,
    fits: (font: number) => boolean,
  ): number => {
    let lo = min;
    let hi = Math.max(min, max);
    let best = min;
    for (let i = 0; i < 18; i++) {
      const mid = (lo + hi) / 2;
      if (fits(mid)) {
        best = mid;
        lo = mid;
      } else {
        hi = mid;
      }
    }
    return Math.max(min, Math.floor(best));
  };

  const nameFitsWith = (nrFont: number, nameFont: number) => {
    const packed = packName(nameFont, maxLines, false);
    if (!packed.complete) return null;
    const height = nameBlockHeight(nameFont, packed.nameLines.length);
    if (nrFont + etikettGap(nrFont, nameFont) + height > boxH + 0.5) return null;
    return { ...packed, height };
  };

  const nrFont = largestFont(MIN_NR_FONT, maxNrByHeight, (font) => {
    if (measure(font, 700, nrText) > boxW + 0.5) return false;
    return nameFitsWith(font, MIN_NAME_FONT) != null;
  });
  const nameFont = largestFont(MIN_NAME_FONT, Math.max(MIN_NAME_FONT, boxH - nrFont), (font) => {
    return nameFitsWith(nrFont, font) != null;
  });
  const fitted = nameFitsWith(nrFont, nameFont);
  const nameLines =
    fitted?.nameLines ??
    wrapEtikettText((text) => measure(nameFont, 600, text), nameText, boxW, maxLines);
  const nameH = fitted?.height ?? nameBlockHeight(nameFont, Math.max(1, nameLines.length));
  const gap = etikettGap(nrFont, nameFont);
  return {
    nrFontPx: nrFont,
    nameFontPx: nameFont,
    nameLines,
    blockHeight: Math.min(boxH, nrFont + gap + nameH),
  };
}

export function buildQrModules(text: string): boolean[][] {
  const qr = createQr(text, { errorCorrectionLevel: "M" });
  const size = qr.modules.size;
  const modules: boolean[][] = [];
  for (let y = 0; y < size; y++) {
    const row: boolean[] = [];
    for (let x = 0; x < size; x++) {
      row.push(Boolean(qr.modules.get(y, x)));
    }
    modules.push(row);
  }
  return modules;
}

export function wrapEtikettTextComplete(
  measure: (text: string) => number,
  text: string,
  maxWidth: number,
  maxLines: number,
): string[] | null {
  const words = text.split(/\s+/).filter(Boolean);
  if (!words.length) return [];
  if (words.some((word) => measure(word) > maxWidth + 0.5)) return null;

  const lines: string[] = [];
  let current = "";
  for (const word of words) {
    const next = current ? `${current} ${word}` : word;
    if (measure(next) <= maxWidth + 0.5) {
      current = next;
      continue;
    }
    if (current) lines.push(current);
    current = word;
    if (lines.length >= maxLines) return null;
  }
  if (current) lines.push(current);
  return lines.length > maxLines ? null : lines;
}

export function wrapEtikettText(
  measure: (text: string) => number,
  text: string,
  maxWidth: number,
  maxLines: number,
): string[] {
  const words = text.split(/\s+/).filter(Boolean);
  if (!words.length) return [];

  const ellipsize = (value: string): string => {
    if (measure(value) <= maxWidth) return value;
    let cut = value;
    while (cut.length > 1 && measure(`${cut}…`) > maxWidth) {
      cut = cut.slice(0, -1);
    }
    return `${cut}…`;
  };

  const lines: string[] = [];
  let current = "";
  for (let i = 0; i < words.length; i++) {
    const word = words[i] ?? "";
    const next = current ? `${current} ${word}` : word;
    if (measure(next) <= maxWidth) {
      current = next;
      continue;
    }
    if (lines.length >= maxLines - 1) {
      lines.push(ellipsize([current, ...words.slice(i)].filter(Boolean).join(" ")));
      return lines;
    }
    if (current) lines.push(current);
    current = word;
  }
  if (current) lines.push(ellipsize(current));
  return lines.slice(0, maxLines);
}

function drawQr(ctx: CanvasRenderingContext2D, modules: boolean[][], x: number, y: number, size: number): void {
  const n = modules.length;
  if (n === 0) return;
  const cell = size / n;
  ctx.fillStyle = "#000";
  for (let row = 0; row < n; row++) {
    const line = modules[row];
    if (!line) continue;
    for (let col = 0; col < n; col++) {
      if (!line[col]) continue;
      ctx.fillRect(x + col * cell, y + row * cell, cell + 0.4, cell + 0.4);
    }
  }
}

export function renderArtikelEtikett(
  item: ArtikelEtikettItem,
  tapeMm: number,
  loc: ArtikelEtikettLocation,
  lengthMm: number,
): RenderedArtikelEtikett {
  const spec = getTapeSpec(tapeMm);
  const layout = planArtikelEtikettLayout(spec, lengthMm);
  const qrUrl = buildArtikelEtikettUrl(item.id, loc);
  const modules = buildQrModules(qrUrl);

  const canvas = document.createElement("canvas");
  canvas.width = layout.widthDots;
  canvas.height = layout.heightDots;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Etikett konnte nicht gezeichnet werden.");

  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  drawQr(ctx, modules, layout.qrX, layout.qrY, layout.qrSize);

  const fonts = fitEtikettFonts(
    (fontPx, weight, text) => {
      ctx.font = `${weight} ${fontPx}px ${ETIKETT_FONT}`;
      return ctx.measureText(text).width;
    },
    item.heroNr,
    item.name,
    layout.textWidth,
    layout.textHeight,
  );
  const textTop = layout.textY + Math.max(0, Math.round((layout.textHeight - fonts.blockHeight) / 2));
  const gap = Math.max(3, Math.round(fonts.nrFontPx * 0.16));

  ctx.fillStyle = "#000";
  ctx.textBaseline = "top";
  ctx.font = `700 ${fonts.nrFontPx}px ${ETIKETT_FONT}`;
  ctx.fillText(item.heroNr.trim() || "—", layout.textX, textTop);

  ctx.font = `600 ${fonts.nameFontPx}px ${ETIKETT_FONT}`;
  const nameTop = textTop + fonts.nrFontPx + gap;
  fonts.nameLines.forEach((line, index) => {
    ctx.fillText(line, layout.textX, nameTop + index * fonts.nameFontPx * LINE_HEIGHT);
  });

  return {
    item,
    qrUrl,
    canvas,
    dataUrl: canvas.toDataURL("image/png"),
    widthDots: canvas.width,
    heightDots: canvas.height,
    widthMm: dotsToMm(canvas.width),
    heightMm: dotsToMm(canvas.height),
  };
}

export function canvasColumnIsDark(canvas: HTMLCanvasElement, x: number, y: number): boolean {
  const ctx = canvas.getContext("2d");
  if (!ctx) return false;
  const pixel = ctx.getImageData(x, y, 1, 1).data;
  const r = pixel[0] ?? 255;
  const g = pixel[1] ?? 255;
  const b = pixel[2] ?? 255;
  const a = pixel[3] ?? 255;
  if (a < 128) return false;
  return (r + g + b) / 3 < 140;
}

export function etikettFileName(item: ArtikelEtikettItem): string {
  const safe = item.heroNr.replace(/[^\w.-]+/g, "_").replace(/^_+|_+$/g, "");
  return `etikett-${safe || item.id}.png`;
}
